import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Link,
  Divider,
  Grid,
} from '@mui/material';
import { NotificationsSection as NotificationsSectionType } from '../../../types/annualReports';
import { useNavigate } from 'react-router-dom';

interface NotificationsSectionProps {
  notifications: NotificationsSectionType;
}

const NotificationsSection: React.FC<NotificationsSectionProps> = ({
  notifications,
}) => {
  const navigate = useNavigate();

  // Handle null values from backend
  const assignedToMe = useMemo(
    () => notifications?.assigned_to_me || [],
    [notifications?.assigned_to_me]
  );
  const needsAttention = notifications?.needs_attention || [];
  const typeTotals = notifications?.type_totals || {};

  // Group assigned forms by form_type
  const groupedForms = useMemo(() => {
    const groups: Record<string, typeof assignedToMe> = {};
    assignedToMe.forEach((form) => {
      if (!groups[form.form_type]) {
        groups[form.form_type] = [];
      }
      groups[form.form_type].push(form);
    });
    return groups;
  }, [assignedToMe]);

  // Get display name for form type
  const getFormTypeDisplayName = (formType: string): string => {
    const names: Record<string, string> = {
      roster_dues: 'Roster & Dues',
      roster_dues_lc: 'Local Chapter Roster & Dues',
      roster_dues_spd: 'S/P/D Chapter Dues',
      irs_lc: 'IRS-LC',
      irs_sc: 'IRS-SC/PC',
      irs_rg: 'IRS-RG',
    };
    return names[formType] || formType.replace('_', ' ').toUpperCase();
  };

  // Get link for "view all" based on form type (LC/SPD roster-dues go to correct list with assigned_to_me)
  const getViewAllLink = (formType: string): string => {
    const links: Record<string, string> = {
      roster_dues:
        '/annual-reports/roster-dues/local-chapters?assigned_to_me=true',
      roster_dues_lc:
        '/annual-reports/roster-dues/local-chapters?assigned_to_me=true',
      roster_dues_spd: '/annual-reports/roster-dues/spd?assigned_to_me=true',
      irs_lc: '/annual-reports/irs-forms/lc?assigned_to_me=true',
      irs_sc: '/annual-reports/irs-forms/sc?assigned_to_me=true',
      irs_rg: '/annual-reports/irs-forms/rg?assigned_to_me=true',
    };
    return links[formType] || '#';
  };

  const totalAssigned = assignedToMe.length;
  const totalCount = Object.values(typeTotals).reduce(
    (sum, count) => sum + count,
    0
  );

  // Split form types into two columns (2 types per column)
  const formTypes = Object.keys(groupedForms);
  const leftColumnTypes = formTypes.slice(0, 2);
  const rightColumnTypes = formTypes.slice(2, 4);

  const renderFormTypeGroup = (
    formType: string,
    forms: typeof assignedToMe,
    isFirstInColumn: boolean
  ) => {
    const totalForType = typeTotals[formType] || forms.length;
    const hasMore = totalForType > forms.length;

    return (
      <Box key={formType} sx={{ mb: 2 }}>
        {!isFirstInColumn && <Divider sx={{ my: 1.5 }} />}
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{ mb: 1, color: 'text.secondary' }}
        >
          {getFormTypeDisplayName(formType)}
        </Typography>
        {forms.map((notification) => (
          <Card
            key={notification.form_id}
            sx={{
              mb: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            onClick={() => navigate(notification.link)}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {notification.form_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notification.status_name}
                  </Typography>
                </Box>
                <Chip
                  label={notification.status_name}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        ))}
        {hasMore && (
          <Box sx={{ mt: 0.5, mb: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate(getViewAllLink(formType))}
              sx={{ cursor: 'pointer' }}
            >
              View all {totalForType} {getFormTypeDisplayName(formType)} forms
            </Link>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          bgcolor: 'rgba(33, 150, 243, 0.08)',
          borderRadius: 1,
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Assigned to Me ({totalCount > 0 ? totalCount : totalAssigned})
        </Typography>
        {totalAssigned > 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {leftColumnTypes.map((formType, index) =>
                renderFormTypeGroup(
                  formType,
                  groupedForms[formType],
                  index === 0
                )
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {rightColumnTypes.map((formType, index) =>
                renderFormTypeGroup(
                  formType,
                  groupedForms[formType],
                  index === 0
                )
              )}
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2">
            No forms are currently assigned to you.
          </Typography>
        )}
      </Box>

      {needsAttention.length > 0 && (
        <Box
          sx={{
            bgcolor: 'warning.light',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Needs Attention ({needsAttention.length})
          </Typography>
          <Box sx={{ mt: 1 }}>
            {needsAttention.map((notification) => (
              <Card
                key={notification.form_id}
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => navigate(notification.link)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {notification.form_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {notification.form_type.replace('_', ' ').toUpperCase()}
                      </Typography>
                    </Box>
                    <Chip
                      label={notification.status_name}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NotificationsSection;
